import {
  ICommunityDetails,
  INeighborhoodDetails,
  ISchoolDetails,
} from "interfaces";
import { captureException } from "scripts/logging/error-report";
import { apiBaseUrl } from "utils/constants";

// Cache expiry time: 1 day in milliseconds
const CACHE_EXPIRY_TIME = 24 * 60 * 60 * 1000; // 24 hours
const MAX_CACHE_SIZE = 1000; // Maximum number of entries per cache

// Cache entry interface with separate timestamps for expiry and LRU
interface CacheEntry<T> {
  data: T;
  fetchedAt: number; // When the data was fetched (for expiry calculation)
  lastAccessedAt: number; // When the data was last accessed (for LRU eviction)
}

// Helper function to check if cache entry is expired (based on fetch time)
const isCacheExpired = (fetchedAt: number): boolean => {
  return Date.now() - fetchedAt > CACHE_EXPIRY_TIME;
};

// Helper function to clean up expired entries from a cache
const cleanupExpiredEntries = <T>(cache: Map<string, CacheEntry<T>>): void => {
  const keysToDelete: string[] = [];
  cache.forEach((entry, key) => {
    if (isCacheExpired(entry.fetchedAt)) {
      keysToDelete.push(key);
    }
  });
  keysToDelete.forEach((key) => cache.delete(key));
};

// Helper function to enforce max cache size using true LRU (remove least recently used entries)
const enforceCacheSize = <T>(
  cache: Map<string, CacheEntry<T>>,
  maxSize: number,
): void => {
  if (cache.size > maxSize) {
    // Sort by lastAccessedAt (least recently used first) and remove oldest entries
    const entries = Array.from(cache.entries()).sort(
      (a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt,
    );
    const entriesToRemove = entries.slice(0, cache.size - maxSize);
    entriesToRemove.forEach(([key]) => cache.delete(key));
  }
};

// Cache for school details to prevent duplicate API calls
const schoolDetailsCache = new Map<string, CacheEntry<ISchoolDetails>>();
const pendingSchoolRequests = new Map<
  string,
  Promise<Record<string, ISchoolDetails>>
>();

// Cache for district school details
const districtSchoolCache = new Map<
  string,
  CacheEntry<ISchoolDetails | null>
>();
const pendingDistrictRequests = new Map<
  string,
  Promise<ISchoolDetails | null>
>();

// Cache for neighborhood details
const neighborhoodCache = new Map<
  string,
  CacheEntry<INeighborhoodDetails | null>
>();
const pendingNeighborhoodRequests = new Map<
  string,
  Promise<INeighborhoodDetails | null>
>();

export const getSchoolsDetailsByIds = async (
  ids: Array<string | number>,
): Promise<Record<string, ISchoolDetails>> => {
  const uniqueIds = Array.from(new Set(ids.map(String))).filter(Boolean);
  if (uniqueIds.length === 0) return {};

  cleanupExpiredEntries(schoolDetailsCache);

  // Check which IDs are already cached and not expired
  const cachedResults: Record<string, ISchoolDetails> = {};
  const idsToFetch: string[] = [];

  uniqueIds.forEach((id) => {
    const cacheEntry = schoolDetailsCache.get(id);
    if (cacheEntry && !isCacheExpired(cacheEntry.fetchedAt)) {
      cachedResults[id] = cacheEntry.data;
      // Update lastAccessedAt for true LRU behavior (doesn't affect expiry)
      cacheEntry.lastAccessedAt = Date.now();
    } else {
      // Remove expired cache entry
      if (cacheEntry) {
        schoolDetailsCache.delete(id);
      }
      idsToFetch.push(id);
    }
  });

  // If all IDs are cached, return immediately
  if (idsToFetch.length === 0) {
    return cachedResults;
  }

  // Create a cache key for the pending request
  const requestKey = idsToFetch.sort().join(",");

  // First, try to find an existing pending batch that can satisfy all required IDs.
  // This handles overlapping-but-non-identical concurrent requests (e.g. [1,2] and [1])
  // so we don't issue redundant network calls for IDs already in-flight.
  for (const [pendingKey, pendingPromise] of pendingSchoolRequests.entries()) {
    const pendingIds = pendingKey.split(",");
    const pendingIdSet = new Set(pendingIds);
    // If every ID we need is already being fetched by this pending batch, reuse it.
    if (idsToFetch.every((id) => pendingIdSet.has(id))) {
      const pendingResult = await pendingPromise;
      const subsetResult: Record<string, ISchoolDetails> = {};
      idsToFetch.forEach((id) => {
        const value = pendingResult[id];
        if (value) {
          subsetResult[id] = value;
        }
      });
      return { ...cachedResults, ...subsetResult };
    }
  }

  // Check if there's already a pending request for exactly these IDs
  if (pendingSchoolRequests.has(requestKey)) {
    const pendingResult = await pendingSchoolRequests.get(requestKey)!;
    return { ...cachedResults, ...pendingResult };
  }

  // Create a new request
  const requestPromise = (async () => {
    try {
      const qs = new URLSearchParams();
      idsToFetch.forEach((id) => qs.append("ids", id));
      const response = await fetch(
        `${apiBaseUrl}/api/v1/chatbot/schools?${qs.toString()}`,
        {
          credentials: "include",
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch school details. Status: ${response.status}`,
        );
      }

      const result = await response.json();

      // Log warning if data is missing on successful response
      if (!result || !result.data) {
        console.warn(
          "Received successful school details response with empty or missing data",
          {
            idsToFetch,
            status: response.status,
            result,
          },
        );
        captureException(
          new Error(
            "Empty or missing 'data' in successful school details response",
          ),
        );
      }

      const list: ISchoolDetails[] = result?.data ?? [];

      // Normalize into a dictionary and cache the results with timestamp
      const currentTime = Date.now();
      const fetchedResults = list.reduce<Record<string, ISchoolDetails>>(
        (acc, item) => {
          if (item?.id) {
            const idString = String(item.id);
            acc[idString] = item;
            schoolDetailsCache.set(idString, {
              data: item,
              fetchedAt: currentTime,
              lastAccessedAt: currentTime,
            });
          }
          return acc;
        },
        {},
      );

      // Enforce cache size after adding new entries to keep it bounded
      enforceCacheSize(schoolDetailsCache, MAX_CACHE_SIZE);

      return fetchedResults;
    } catch (error) {
      console.error("Error fetching multiple school details:", error);
      captureException(error);
      return {};
    } finally {
      // Clean up the pending request
      pendingSchoolRequests.delete(requestKey);
    }
  })();

  // Store the pending request
  pendingSchoolRequests.set(requestKey, requestPromise);

  const fetchedResults = await requestPromise;
  return { ...cachedResults, ...fetchedResults };
};

export const getDistrictSchoolDetailsById = async (
  districtId: string | number,
): Promise<ISchoolDetails | null> => {
  const idString = String(districtId);

  // Periodic cleanup (size enforcement moved to after cache hit/write)
  cleanupExpiredEntries(districtSchoolCache);

  // Check cache first and verify it's not expired
  const cacheEntry = districtSchoolCache.get(idString);
  if (cacheEntry && !isCacheExpired(cacheEntry.fetchedAt)) {
    // Update lastAccessedAt for true LRU behavior (doesn't affect expiry)
    cacheEntry.lastAccessedAt = Date.now();
    return cacheEntry.data;
  }

  // Remove expired cache entry
  if (cacheEntry) {
    districtSchoolCache.delete(idString);
  }

  // Check if there's a pending request
  if (pendingDistrictRequests.has(idString)) {
    return await pendingDistrictRequests.get(idString)!;
  }

  // Create new request
  const requestPromise = (async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/v1/chatbot/school/district/${districtId}`,
        {
          credentials: "include",
        },
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(
          `Failed to fetch district school details. Status: ${response.status}`,
        );
      }

      const result = await response.json();

      // Handle empty successful response - don't cache, just return null, but log for debugging
      if (!result || !result.data) {
        console.warn(
          "Received successful district school details response with empty or missing data",
          {
            districtId,
            status: response.status,
            result,
          },
        );
        captureException(
          new Error(
            "Empty or missing 'data' in successful district school details response",
          ),
        );
        return null;
      }

      const schoolDetails = result.data as ISchoolDetails;
      const currentTime = Date.now();
      districtSchoolCache.set(idString, {
        data: schoolDetails,
        fetchedAt: currentTime,
        lastAccessedAt: currentTime,
      });
      // Enforce cache size immediately after adding entry
      enforceCacheSize(districtSchoolCache, MAX_CACHE_SIZE);
      return schoolDetails;
    } catch (error) {
      console.error("Error fetching district school details:", error);
      captureException(error);
      // Don't cache errors - return null but allow retry on next call
      return null;
    } finally {
      pendingDistrictRequests.delete(idString);
    }
  })();

  pendingDistrictRequests.set(idString, requestPromise);
  return await requestPromise;
};

type NeighborhoodIdType = "ptId" | "nid";

export const ID_TYPE_TO_QUERY_PARAM: Record<NeighborhoodIdType, string> = {
  ptId: "pt_id",
  nid: "nid",
};

export const getNeighborhoodDetailsById = async (
  id: string | number,
  idType: NeighborhoodIdType = "ptId", // ← default "ptId" — purana behavior same rahega
): Promise<INeighborhoodDetails | null> => {
  const idString = `${idType}:${String(id)}`; // ← "ptId:123" — type bhi cache key mein

  // Periodic cleanup (size enforcement moved to after cache hit/write)
  cleanupExpiredEntries(neighborhoodCache);

  // Check cache first and verify it's not expired
  const cacheEntry = neighborhoodCache.get(idString);
  if (cacheEntry && !isCacheExpired(cacheEntry.fetchedAt)) {
    // Update lastAccessedAt for true LRU behavior (doesn't affect expiry)
    cacheEntry.lastAccessedAt = Date.now();
    return cacheEntry.data;
  }

  // Remove expired cache entry
  if (cacheEntry) {
    neighborhoodCache.delete(idString);
  }

  // Check if there's a pending request
  if (pendingNeighborhoodRequests.has(idString)) {
    return await pendingNeighborhoodRequests.get(idString)!;
  }

  // Build query param based on idType
  const queryParam = ID_TYPE_TO_QUERY_PARAM[idType]; // e.g. ptId → "pt_id"

  // Create new request
  const requestPromise = (async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/v1/chatbot/neighborhoods/summary?${queryParam}=${id}`,
        {
          credentials: "include",
        },
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(
          `Failed to fetch neighborhood details. Status: ${response.status}`,
        );
      }

      const result = await response.json();

      // Handle empty successful response - don't cache, just return null, but log for debugging
      if (!result || !result.data) {
        console.warn(
          "Received successful neighborhood details response with empty or missing data",
          { id, idType, status: response.status, result },
        );
        captureException(
          new Error(
            "Empty or missing 'data' in successful neighborhood details response",
          ),
        );
        return null;
      }

      const neighborhood = result.data as INeighborhoodDetails;
      const currentTime = Date.now();
      neighborhoodCache.set(idString, {
        data: neighborhood,
        fetchedAt: currentTime,
        lastAccessedAt: currentTime,
      });
      // Enforce cache size immediately after adding entry
      enforceCacheSize(neighborhoodCache, MAX_CACHE_SIZE);
      return neighborhood;
    } catch (error) {
      console.error("Error fetching neighborhood details:", error);
      captureException(error);
      // Don't cache errors - return null but allow retry on next call
      return null;
    } finally {
      pendingNeighborhoodRequests.delete(idString);
    }
  })();

  pendingNeighborhoodRequests.set(idString, requestPromise);
  return await requestPromise;
};

const communityCache = new Map<string, CacheEntry<ICommunityDetails | null>>();
const pendingCommunityRequests = new Map<
  string,
  Promise<ICommunityDetails | null>
>();

export const getCommunityDetailsById = async (
  communityId: string | number,
): Promise<ICommunityDetails | null> => {
  const idString = String(communityId);

  cleanupExpiredEntries(communityCache);

  const cacheEntry = communityCache.get(idString);
  if (cacheEntry && !isCacheExpired(cacheEntry.fetchedAt)) {
    cacheEntry.lastAccessedAt = Date.now();
    return cacheEntry.data;
  }

  if (cacheEntry) {
    communityCache.delete(idString);
  }

  if (pendingCommunityRequests.has(idString)) {
    return await pendingCommunityRequests.get(idString)!;
  }

  const requestPromise = (async () => {
    try {
      const response = await fetch(
        `${apiBaseUrl}/api/v1/chatbot/community/summary/${encodeURIComponent(idString)}`,
        {
          credentials: "include",
        },
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(
          `Failed to fetch community details. Status: ${response.status}`,
        );
      }

      const result = await response.json();

      if (!result || !result.data) {
        console.warn(
          "Received successful community details response with empty or missing data",
          { communityId, status: response.status, result },
        );
        captureException(
          new Error(
            "Empty or missing 'data' in successful community details response",
          ),
        );
        return null;
      }

      const community = result.data as ICommunityDetails;
      const currentTime = Date.now();
      communityCache.set(idString, {
        data: community,
        fetchedAt: currentTime,
        lastAccessedAt: currentTime,
      });
      enforceCacheSize(communityCache, MAX_CACHE_SIZE);
      return community;
    } catch (error) {
      console.error("Error fetching community details:", error);
      captureException(error);
      return null;
    } finally {
      pendingCommunityRequests.delete(idString);
    }
  })();

  pendingCommunityRequests.set(idString, requestPromise);
  return await requestPromise;
};
